import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { PageLayout } from 'common/components';
import * as partnershipApi from 'client/partnership/api';
import { clearIBDraft, loadIBDraft } from '../BecomeIntroducingBrokerPage/ibDraftStorage';
import { staticStyles } from './style';

const hasIBContractInfo = contractInfo => {
  if (!contractInfo) return false;
  if (typeof contractInfo === 'object') return Object.keys(contractInfo).length > 0;
  return true;
};

const IBContractPageContent = ({ profile }) => {
  const history = useHistory();
  const userId = profile.id;
  const isContractPending = profile.isIbContractPending || profile.isIbPartnerAccountContractPending;
  const canGoBackToQuestionnaire = profile.isIBInvite && !isContractPending;
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [blockchainNetwork, setBlockchainNetwork] = useState('TRC20');
  const [walletError, setWalletError] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contractPreviewUrl, setContractPreviewUrl] = useState('');

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext('2d');
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasSignature(false);
    setSignatureError(false);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) clearSignature();
    setAttachments([]);
    loadIBDraft(userId).then(draft => {
      if (draft && Array.isArray(draft.attachments)) setAttachments(draft.attachments);
    });
  }, [clearSignature, userId]);

  useEffect(() => {
    let active = true;
    let objectUrl;

    partnershipApi
      .getIBContractPreview()
      .then(response => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setContractPreviewUrl(objectUrl);
      })
      .catch(error => {
        console.error('Failed to load IB contract preview:', error);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId]);

  const getPoint = event => {
    const canvas = canvasRef.current;
    const bounds = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - bounds.left) / bounds.width) * canvas.width,
      y: ((event.clientY - bounds.top) / bounds.height) * canvas.height,
    };
  };

  const beginDrawing = event => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const point = getPoint(event);
    drawingRef.current = true;
    canvas.setPointerCapture(event.pointerId);
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const drawSignature = event => {
    if (!drawingRef.current) return;
    const context = canvasRef.current.getContext('2d');
    const point = getPoint(event);
    context.lineTo(point.x, point.y);
    context.stroke();
    setHasSignature(true);
  };

  const finishDrawing = () => {
    drawingRef.current = false;
  };

  const handleSubmit = async () => {
    if (!hasSignature) {
      setSignatureError(true);
      return;
    }
    const missingWallet = !walletAddress.trim() || !blockchainNetwork.trim();
    if (missingWallet) {
      setWalletError(true);
      setShowWalletModal(true);
      return;
    }

    setIsSubmitting(true);
    try {
      await partnershipApi.submitIBContract({
        signature: canvasRef.current.toDataURL('image/png'),
        walletAddress: walletAddress.trim(),
        blockchainNetwork: blockchainNetwork.trim(),
        attachments,
      });
      await clearIBDraft(userId);
      window.location.href = '/partnership/accounts';
    } catch (error) {
      const responseData = error.response && error.response.data;
      if (responseData && (responseData.wallet || responseData.detail === 'Cryptocurrency wallet is required')) {
        setWalletError(true);
        setShowWalletModal(true);
      } else if (responseData && responseData.signature) {
        setSignatureError(true);
      } else {
        console.error('Failed to submit IB contract:', error);
        alert('An error occurred while submitting your application. Please try again later.');
      }
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout externalClass="IBContractPageLayout">
      <div className="IBContract">
        <h1 className="IBContract__title">Review &amp; Sign the IB Agreement</h1>
        <p className="IBContract__subtitle">
          Please review the agreement below. Once you confirm the details are correct, sign at the bottom to complete
          your IB application.
        </p>

        <iframe
          className="IBContract__document"
          title="IB Agreement"
          src={contractPreviewUrl ? `${contractPreviewUrl}#view=FitH&readonly=1` : undefined}
        />

        <section className={`IBContract__signature-section${signatureError ? ' IBContract__section--error' : ''}`}>
          <h2>
            Customer Signature <span>(Required)</span>
          </h2>
          <canvas
            ref={canvasRef}
            width="1000"
            height="300"
            className="IBContract__signature-pad"
            onPointerDown={beginDrawing}
            onPointerMove={drawSignature}
            onPointerUp={finishDrawing}
            onPointerCancel={finishDrawing}
            onPointerLeave={finishDrawing}
          />
          <button type="button" className="IBContract__clear-button" onClick={clearSignature}>
            Clear / Redraw
          </button>
        </section>

        <section className={`IBContract__wallet-section${walletError ? ' IBContract__section--error' : ''}`}>
          <h2>Cryptocurrency Wallet of Introducing Broker</h2>
          <label htmlFor="ib-wallet-address">USDT Wallet Address</label>
          <input
            id="ib-wallet-address"
            type="text"
            value={walletAddress}
            onChange={event => {
              setWalletAddress(event.target.value);
              setWalletError(false);
            }}
          />
          <label htmlFor="ib-blockchain-network">Blockchain Network</label>
          <select
            id="ib-blockchain-network"
            value={blockchainNetwork}
            onChange={event => {
              setBlockchainNetwork(event.target.value);
              setWalletError(false);
            }}>
            <option value="TRC20">TRC20</option>
          </select>
        </section>

        <div className="IBContract__actions">
          {canGoBackToQuestionnaire && (
            <button
              type="button"
              className="IBContract__back-button"
              onClick={() => history.push('/partnership/become_introducing_broker')}>
              Back
            </button>
          )}
          <button type="button" className="IBContract__submit-button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </div>

      {showWalletModal && (
        <div className="IBContract__modal-backdrop" role="presentation">
          <div className="IBContract__modal" role="dialog" aria-modal="true" aria-labelledby="ib-wallet-modal-title">
            <h2 id="ib-wallet-modal-title">Filling Tip</h2>
            <p>Please complete Cryptocurrency Wallet of Introducing Broker.</p>
            <button type="button" onClick={() => setShowWalletModal(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      <style jsx>{staticStyles}</style>
    </PageLayout>
  );
};

const IBContractPage = ({ profile }) => {
  if (hasIBContractInfo(profile.ibContractInfo)) return <Redirect to="/" />;

  return <IBContractPageContent profile={profile} />;
};

export { IBContractPage };

IBContractPage.propTypes = {
  profile: PropTypes.object,
};

IBContractPage.defaultProps = {
  profile: {},
};

IBContractPageContent.propTypes = {
  profile: PropTypes.object,
};

IBContractPageContent.defaultProps = {
  profile: {},
};

export default connect(state => ({ profile: state.user.profile || {} }))(IBContractPage);
